/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Footer component for TariffsAndServices
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.15.0
 * @since 1.4.0
 */

import { Fragment, useCallback } from 'react';

import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { useCheckCustomRole } from '@utils/useCheckCustomRole';

import BackButton from '../../../components/common/BackButton/BackButton';
import { ButtonStyled } from '../../../components/common/Button/styled/ButtonStyled';
import GlobalContainer from '../../../components/common/GlobalContainer/GlobalContainer';
import ArrowTooltip from '../../../components/common/Tooltip/Tooltip';
import { breadCrumbsGoBackAction } from '../../../modules/breadCrumbs/breadCrumbsActions';
import { makeSelectBreadCrumbsData } from '../../../modules/breadCrumbs/breadCrumbsSelectors';
import { IBreadCrumb } from '../../../modules/breadCrumbs/breadCrumbsTypes';
import { makeSelectTranslationPageProducts } from '../../../modules/internationalization/intSelectors';
import { IPageProductsTranslation } from '../../../modules/internationalization/types/pages/products';
import { ONBOARDING_DATA_ATTR } from '../../../modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '../../../modules/onboarding/types/onboardingTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { IRouteParams } from '../../../routes/constants/routesConstants';
import { productsConnectSelectedServicesAction } from '../tariffsAndServicesActions';
import {
    makeSelectProductsAllowedManageProducts,
    makeSelectProductsButtonProductManageTitle,
    makeSelectProductsButtonProductManageTooltipMessage,
    makeSelectProductsCategorySelected,
    makeSelectProductsIsButtonProductManageDisabled,
    makeSelectProductsIsRateplans,
    makeSelectProductsItemsSelectedIds,
    makeSelectProductsMeta,
    makeSelectTariffsAndServicesAllowedConnectTariff,
} from '../tariffsAndServicesSelectors';
import { IProductsConnectSelectedServicesPayload, IProductsMeta } from '../tariffsAndServicesTypes';
import {
    PageTariffsAndServicesFooterFixedContainer,
    PageTariffsAndServicesFooterStickyContainer,
    PageTariffsAndServicesFooterStyled,
    PageTariffsAndServicesShadowStub,
} from './styled/PageTariffsAndServicesStyled';

interface ITariffsAndServicesFooterProps {
    translation: IPageProductsTranslation;
}

export type TTariffsAndServicesFooterProps = ITariffsAndServicesFooterProps & IConnectedProps & IDispatchProps;

export const ProductsFooter = (props: TTariffsAndServicesFooterProps) => {
    const { isRateplans, productsMeta } = props;
    const { clientId } = useParams<IRouteParams>();
    const customRole = isRateplans ? AuthorityRoleEnum.ROLE_RATEPLAN_MANAGE : AuthorityRoleEnum.ROLE_SERVICES_MANAGE;
    const isCustomRoleCheckAvailable = useCheckCustomRole(false, customRole);

    const handleClickConnect = () => {
        if (props.isButtonProductManageDisabled) return;

        props.connectSelectedServices({
            clientId,
        });
    };

    const allowConnect = useCallback(() => {
        if (props.categorySelected === productsMeta.tariffsCategoryId && !props.allowedConnectTariff) {
            return false;
        }
        return props.allowedManageProducts;
    }, [props.allowedConnectTariff, props.allowedManageProducts, props.categorySelected, productsMeta]);

    return (
        <Fragment>
            {/* Block with shadow on the right side of footer */}
            <PageTariffsAndServicesFooterFixedContainer>
                <GlobalContainer>
                    <PageTariffsAndServicesShadowStub/>
                </GlobalContainer>
            </PageTariffsAndServicesFooterFixedContainer>

            <PageTariffsAndServicesFooterStickyContainer>
                <PageTariffsAndServicesFooterStyled>
                    <div>
                        {
                            props.breadcrumbsData.length > 0 &&
                            <BackButton title={props.translation.buttonBack.title}
                                        onClick={props.breadCrumbsGoBack}/>
                        }
                    </div>
                    <div>
                        {
                            (allowConnect() || isCustomRoleCheckAvailable) &&
                            <ArrowTooltip
                                disableHoverListener={!props.isButtonProductManageDisabled ||
                                !props.itemsSelected.length}
                                placement={'top'}
                                title={props.buttonProductManageTooltipMessage}
                            >
                                <ButtonStyled
                                    className={props.isButtonProductManageDisabled ? 'disabled' : ''}
                                    kind={'mediumNormal'}
                                    onClick={handleClickConnect}
                                    {...{ [ONBOARDING_DATA_ATTR]: EOnboardingComponentsId.productBtnConnect }}
                                >
                                    {props.buttonProductManageTitle}
                                </ButtonStyled>
                            </ArrowTooltip>
                        }
                    </div>
                </PageTariffsAndServicesFooterStyled>
            </PageTariffsAndServicesFooterStickyContainer>
        </Fragment>
    );
};

interface IConnectedProps {
    allowedConnectTariff: boolean;
    allowedManageProducts: boolean;
    breadcrumbsData: IBreadCrumb[];
    buttonProductManageTitle: string;
    buttonProductManageTooltipMessage: string;
    categorySelected: number;
    isButtonProductManageDisabled: boolean;
    isRateplans: boolean;
    itemsSelected: number[];
    productsMeta: IProductsMeta;
    translation: IPageProductsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    allowedConnectTariff: makeSelectTariffsAndServicesAllowedConnectTariff(),
    allowedManageProducts: makeSelectProductsAllowedManageProducts(),
    breadcrumbsData: makeSelectBreadCrumbsData(),
    buttonProductManageTitle: makeSelectProductsButtonProductManageTitle(),
    buttonProductManageTooltipMessage: makeSelectProductsButtonProductManageTooltipMessage(),
    categorySelected: makeSelectProductsCategorySelected(),
    isButtonProductManageDisabled: makeSelectProductsIsButtonProductManageDisabled(),
    isRateplans: makeSelectProductsIsRateplans(),
    itemsSelected: makeSelectProductsItemsSelectedIds(),
    productsMeta: makeSelectProductsMeta(),
    translation: makeSelectTranslationPageProducts(),
});

interface IDispatchProps {
    connectSelectedServices: (p: IProductsConnectSelectedServicesPayload) => void;
    breadCrumbsGoBack: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    breadCrumbsGoBack: () => dispatch(breadCrumbsGoBackAction()),
    connectSelectedServices: (p) => dispatch(productsConnectSelectedServicesAction(p)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductsFooter);
